import { useMemo } from 'react';
import { DocumentNode } from 'graphql';
import { ApolloClient, from, InMemoryCache, NormalizedCacheObject, split } from '@apollo/client';
import { ContentfulRestLink } from '@goodrx/x-core';
import possibleTypes from '@goodrx/x-core/dist/cjs/types/possibleTypes.json';
import { ContentfulCredentials } from './ContentfulCredentials';
import { contentfulLink } from './contentfulLink';
import { errorLink } from './errorLink';

let apolloClient: ApolloClient<NormalizedCacheObject>;

interface PreviewOptions {
  preview?: boolean;
}

function createApolloClient(options: PreviewOptions) {
  const { preview } = options || { preview: false };
  const requestLink = split(
    (operation) => operation.getContext().restLink,
    new ContentfulRestLink(ContentfulCredentials, { include: 10 }),
    contentfulLink({
      ...ContentfulCredentials,
      preview,
    })
  );

  return new ApolloClient({
    ssrMode: typeof window === 'undefined',
    link: from([errorLink, requestLink]),
    cache: new InMemoryCache({ possibleTypes }),
  });
}

export function initializeApollo(initialState: DocumentNode | undefined, options: PreviewOptions) {
  const useApolloClient = apolloClient ?? createApolloClient(options);

  // If your page has Next.js data fetching methods that use Apollo Client, the initial state
  // gets hydrated here
  if (initialState) {
    const existingCache = useApolloClient.extract();
    useApolloClient.cache.restore({ ...existingCache, ...((initialState || {}) as Record<string, any>) });
  }
  // For SSG and SSR always create a new Apollo Client
  if (typeof window === 'undefined') return useApolloClient;

  // Create the Apollo Client once in the client
  if (!apolloClient) apolloClient = useApolloClient;

  return useApolloClient;
}

export function useApollo(initialState: DocumentNode | undefined, options: PreviewOptions) {
  const store = useMemo(() => initializeApollo(initialState, options), [initialState, options]);
  return store;
}
